/**
 * Re-read a lesson you have already done, in either grammar course.
 *
 * The rule you want three weeks later is the one you cannot get to: the daily
 * session shows today's lesson and then it is gone. This is the same lesson
 * screen the session uses, with the button pointing back instead of forward, so
 * there is one rendering of a lesson rather than two that drift.
 *
 * Reachable only from a completed row in the progress list. Nothing stops a
 * hand-typed URL to a future lesson, and nothing needs to — skipping ahead in
 * your own course is not an attack, it is just a worse way to learn.
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';

import { grammarCourseFor } from '@/apps';
import { GrammarLessonScreen } from '@/apps/shared/grammar/LessonScreen';
import { api } from '@/lib/api';
import { useAuxLanguages } from '@/lib/queries';

export function GrammarLessonRoute() {
  const { appId, n } = useParams<{ appId: string; n: string }>();
  const navigate = useNavigate();
  const lessonN = Number(n);
  const { primaryLang } = useAuxLanguages();
  const binding = grammarCourseFor(appId);

  const lessonQuery = useQuery<unknown>({
    queryKey: [appId, 'lesson', lessonN],
    queryFn: () => api.grammarLesson<unknown>(appId as string, lessonN),
    enabled: Number.isFinite(lessonN) && binding !== null,
  });

  // A URL naming an app that is not a grammar course. Nothing to render, and
  // nothing to fetch either — the query above stays disabled.
  if (!binding) {
    return (
      <main className="mx-auto w-full max-w-2xl px-4 py-6">
        <p className="text-destructive">That app has no lessons.</p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-6">
      <Link
        to={`/apps/${appId}`}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 self-start text-sm"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {binding.displayName}
      </Link>

      {lessonQuery.isPending && <p className="text-muted-foreground">Loading…</p>}
      {lessonQuery.isError && <p className="text-destructive">That lesson could not be loaded.</p>}

      {lessonQuery.data !== undefined && (
        <GrammarLessonScreen
          lesson={binding.toLessonView(lessonQuery.data)}
          course={binding.course}
          primaryLang={primaryLang}
          startLabel={`Back to ${binding.displayName}`}
          onStart={() => navigate(`/apps/${appId}`)}
        />
      )}
    </main>
  );
}
